import { DataTable, FormInput, FormLabel, FormSectionHeader, FormSelect, Heading, Loader } from "@/components";
import { Button, Card, CardContent, Checkbox } from "@/components/ui";
import usePropertyReport from "./usePropertyReport";
import { FormProvider } from "react-hook-form";
import { BLOCK, COMMUNITY, OCCUPANCY, occupancyOptions, SEARCH, ZONE } from "./consts";
import { allOption } from "@/lib/utils";
import { Download } from "lucide-react";
import { OccupantsDialog } from "./OccupantsDialog";
import { OutstandingDueDialog } from "./OutstandingDueDialog";
import { IncidentDialog } from "./IncidentDialog";

const PropertyReport = () => {
  const { form, isLoadingCommunity, isLoadingZoneList, isBlockLoading, communityList,
    zoneList, propertyBlock, columns, onOutstandingCheckedChange, outstandingDues, onClearAll,
    isLoadingReport, onSubmit, reportData, pageNumber, setPageNumber, pageSize, setPageSize,
    sorting, setSorting,
    openOccupantDialog, setOpenOccupantDialog, selectedRow, openOutstandingDialog,
    setOpenOutstandingDialog, openIncidentDialog, setOpenIncidentDialog, formData,
    onExport, isLoadingExportReport
   } = usePropertyReport();

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-6">
        <Heading
          title="Property Report"
        />
      </div>

      <Card className="shadow-none rounded-md">
        <CardContent>
          <FormSectionHeader>Find Property</FormSectionHeader>

          {isLoadingCommunity ? <Loader /> : 
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[20px]">
                  <FormSelect
                    multiSelect
                    name={COMMUNITY}
                    control={form.control}
                    label="Community"
                    placeholder="Please select"
                    options={[...allOption, ...communityList?.map((data: any) => ({
                      value: data?.communityId?.toString(),
                      label: data?.name,
                    }))]}
                  />
                  <FormSelect
                    multiSelect
                    name={ZONE}
                    control={form.control}
                    label="Zone"
                    placeholder="Please select"
                    options={[...allOption, ...zoneList
                      ?.filter((data: any) => formData[COMMUNITY] == "all" || formData[COMMUNITY]?.includes(data.communityId?.toString()))
                      ?.map((data: any) => ({
                      value: data?.zoneId?.toString(),
                      label: data?.zoneName,
                    }))]}
                    disabled={isLoadingZoneList}
                  />
                  <FormSelect
                    multiSelect
                    name={BLOCK}
                    control={form.control}
                    label="Block"
                    placeholder="Please select"
                    options={[...allOption, ...propertyBlock
                      ?.filter((data: any) => formData[ZONE] == "all" || formData[ZONE]?.includes(data.zoneId?.toString()))
                      ?.map((data: any) => ({
                      value: data?.blockId?.toString(),
                      label: data?.blockName,
                    }))]}
                    disabled={isBlockLoading}
                  />
                  <FormSelect
                    multiSelect
                    name={OCCUPANCY}
                    control={form.control}
                    label="Occupancy"
                    placeholder="Please select"
                    options={[...allOption, ...occupancyOptions]}
                  />
                  <div className="lg:col-span-2">
                    <FormInput
                      name={SEARCH}
                      control={form.control}
                      label="Property Search"
                      placeholder="Please enter"
                    />
                  </div>
                  <div className="lg:col-span-2 flex flex-row gap-3 lg:items-center lg:justify-end">
                    <Button>Search</Button>
                    <Button variant="outline" type="button" onClick={onClearAll}>Clear All</Button>
                  </div>
                </div>
              </form>
            </FormProvider>
          }
        </CardContent>
      </Card>

      <div className="flex flex-row justify-between my-5">
        <div />
        <div className="flex flex-row items-center justify-end gap-3">
          <div className="flex flex-row items-center">
            <Checkbox
              className="mr-1 bg-white"
              onCheckedChange={onOutstandingCheckedChange}
              checked={outstandingDues}
            />
            <FormLabel isCheckbox className="bg-transparent">Outstanding dues only</FormLabel>
          </div>
          <Button className="bg-green-700 hover:bg-green-700" type="button" onClick={onExport} disabled={isLoadingExportReport}>
            {isLoadingExportReport ? <Loader variant="secondary" /> : <Download />} Bulk Download
          </Button>
        </div>
      </div>

      {isLoadingReport ? <Loader /> 
        : <DataTable
            data={reportData?.data || []}
            columns={columns}
            pageSize={pageSize}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            totalPages={Math.ceil(reportData?.total / pageSize)}
            totalRecords={reportData?.total}
            serverSidePagination={true}
            sorting={sorting}
            onSortingChange={setSorting}
          />
      }

      {openOccupantDialog &&
        <OccupantsDialog
          open={openOccupantDialog}
          setOpen={setOpenOccupantDialog}
          selectedRow={selectedRow}
        />}
      {openOutstandingDialog &&
        <OutstandingDueDialog
          open={openOutstandingDialog}
          setOpen={setOpenOutstandingDialog}
          selectedRow={selectedRow}
        />}
      {openIncidentDialog &&
        <IncidentDialog
          open={openIncidentDialog}
          setOpen={setOpenIncidentDialog}
          selectedRow={selectedRow}
        />}
    </div>
  )
}

export default PropertyReport;